import { useEffect, useRef, useState } from "react";
import { Modal } from "@JohnLewisPartnership/jl-design-system/dist/components/modal/Modal";
import { Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";
import { PrimaryButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { Product } from "@~/types/types";
import { useVariant } from "@~/contexts/VariantContext/VariantContext";
import {
  WISHLIST_GIFT_MESSAGE,
  WISHLIST_GIFT_MESSAGE_ERROR,
  WISHLIST_GIFT_MESSAGE_SUCCESS,
} from "@~/utils/events";
import styles from "./GiftMessageModal.module.scss";

type GiftMessageModalProps = {
  product: Product;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  portalContainer?: Element | null;
};

export function GiftMessageModal({
  product,
  isOpen,
  onClose,
  onConfirm,
  portalContainer,
}: GiftMessageModalProps) {
  const { variant } = useVariant();
  const [from, setFrom] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const listenersRef = useRef<{
    onSuccess: EventListener;
    onError: EventListener;
  } | null>(null);

  const imageUrl = variant?.images?.primary?.split("?")[0] || "";
  const skuID = product.aliases.pimProductId;

  // Clean up listeners and abort signal on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      if (listenersRef.current) {
        window.removeEventListener(
          WISHLIST_GIFT_MESSAGE_SUCCESS,
          listenersRef.current.onSuccess,
        );
        window.removeEventListener(
          WISHLIST_GIFT_MESSAGE_ERROR,
          listenersRef.current.onError,
        );
      }
    };
  }, []);

  const handleSubmit = () => {
    setIsSubmitting(true);
    setSubmitError(null);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    function onSuccess(e: Event) {
      if (controller.signal.aborted) return;
      const detail = (e as CustomEvent<{ skuID: string }>).detail;
      if (detail.skuID !== skuID) return;
      cleanup();
      setIsSubmitting(false);
      onConfirm();
    }

    function onError(e: Event) {
      if (controller.signal.aborted) return;
      const detail = (e as CustomEvent<{ skuID: string; message?: string }>)
        .detail;
      if (detail.skuID !== skuID) return;
      cleanup();
      setIsSubmitting(false);
      setSubmitError(
        detail.message ?? "Something went wrong, please try again later.",
      );
    }

    listenersRef.current = { onSuccess, onError };

    const timeoutId = setTimeout(() => {
      if (controller.signal.aborted) return;
      cleanup();
      setIsSubmitting(false);
      setSubmitError("Something went wrong, please try again later.");
    }, 8000);

    function cleanup() {
      controller.abort();
      if (listenersRef.current) {
        window.removeEventListener(
          WISHLIST_GIFT_MESSAGE_SUCCESS,
          listenersRef.current.onSuccess,
        );
        window.removeEventListener(
          WISHLIST_GIFT_MESSAGE_ERROR,
          listenersRef.current.onError,
        );
      }
      clearTimeout(timeoutId);
    }

    window.addEventListener(WISHLIST_GIFT_MESSAGE_SUCCESS, onSuccess);
    window.addEventListener(WISHLIST_GIFT_MESSAGE_ERROR, onError);

    window.dispatchEvent(
      new CustomEvent(WISHLIST_GIFT_MESSAGE, {
        detail: { skuID, from, message },
      }),
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      title="Write a message"
      onClose={onClose}
      footer={false}
      className={styles.modal}
      portalContainer={portalContainer || undefined}
    >
      <div className={styles.content}>
        {imageUrl && (
          <div className={styles.imageColumn}>
            <img
              src={imageUrl}
              alt={product.title}
              className={styles.productImage}
              data-testid="gift-message-product-image"
            />
          </div>
        )}
        <div className={styles.formColumn}>
          <p className={styles.description}>
            Let them know who it&apos;s from with a gift message. We&apos;ll add
            it to their list to read when you check out.
          </p>
          <div className={styles.field}>
            <label htmlFor="giftMessageFrom">From</label>
            <input
              id="giftMessageFrom"
              name="giftMessageFrom"
              type="text"
              className={styles.input}
              spellCheck
              autoComplete="name"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              data-testid="gift-message-from"
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="giftMessageBody">Message</label>
            <textarea
              id="giftMessageBody"
              name="giftMessageBody"
              className={styles.textarea}
              spellCheck
              autoComplete="off"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="gift-message-body"
            />
          </div>
          {submitError && (
            <p
              aria-live="assertive"
              className={styles.error}
              data-testid="gift-message-error"
            >
              {submitError}
            </p>
          )}
          <PrimaryButton
            submitting={isSubmitting}
            onClick={handleSubmit}
            data-testid="gift-message-submit"
            className={styles.submitButton}
          >
            Add to basket
          </PrimaryButton>
        </div>
      </div>
      <Message
        data-testid={"delivery-notice"}
        className={styles.deliveryNotice}
        type="information"
        title={"Delivery of gifts"}
        whiteBackground={true}
        wideTab={true}
        body={
          <div className={styles.deliveryBody}>
            <p>The wish list does not store any address details.</p>
            <p>
              Ask the wish list owner for the address where they want gifts
              sent. You will need to enter it at checkout.
            </p>
            <p>
              Alternatively, gifts can be delivered to your address or one of
              1000s of Click & Collect locations.
            </p>
          </div>
        }
      />
    </Modal>
  );
}
